<template>
  <div class="container flex-grow-1 d-flex flex-column my-4">
    <Loading v-if="loading" />

    <EmptyState
      v-else-if="hasErrorWithoutData"
      :icon="['far', 'triangle-exclamation']"
      :message="errorMessage"
    >
      <template #actions>
        <button
          type="button"
          class="btn btn-outline-primary text-nowrap"
          @click="reloadTools"
        >
          {{ $t("settings.reload") }}
        </button>
      </template>
    </EmptyState>

    <EmptyState
      v-else-if="hasNoTools"
      :icon="['far', 'grid-2-plus']"
      :message="$t('settings.no_tools_message')"
    >
      <template #actions>
        <button
          class="btn btn-primary text-white"
          v-if="isSuperAdmin"
          @click="openModalCreate()"
        >
          <font-awesome-icon
            class="me-2"
            :icon="['far', 'plus']"
          />
          {{ $t("settings.new_tool") }}
        </button>
      </template>
    </EmptyState>

    <template v-else>
      <div
        class="d-flex flex-column flex-lg-row gap-3 align-items-stretch align-items-lg-center justify-content-between mb-4"
      >
        <ToolSearchBar v-model="searchTerm" />
        <ToolToolbar
          v-model:show-icons="showIcons"
          v-model:view-mode="listOrCard"
          :show-new-button="isSuperAdmin"
          @new-tool="openModalCreate"
        />
      </div>

      <div
        v-if="error"
        class="alert alert-warning d-flex align-items-center justify-content-between gap-3"
        role="alert"
      >
        <span>{{ errorMessage }}</span>
        <button
          type="button"
          class="btn btn-sm btn-outline-warning"
          @click="reloadTools"
        >
          {{ $t("settings.reload") }}
        </button>
      </div>

      <div
        v-if="hasNoFilteredResults"
        class="alert alert-secondary text-center"
        role="status"
      >
        {{ $t("settings.no_filtered_results") }}
      </div>
      <div v-else>
        <div
          v-if="listOrCard === 'card'"
          class="row"
        >
          <div
            class="col col-md-6 col-xl-3 col-12 mb-4 d-flex"
            v-for="item in filteredTools"
            :key="item.id"
          >
            <ToolCard
              :tool="item"
              :show-icons="showIcons"
              :is-super-admin="isSuperAdmin"
              :is-admin="isAdmin"
              @settings="openSettings"
              @delete="handleDeleteClick"
              @users="handleUsersClick"
            />
          </div>
        </div>

        <div
          class="row"
          v-else
        >
          <div
            class="col-12 mb-3"
            v-for="item in filteredTools"
            :key="item.id"
          >
            <ToolListItem
              :tool="item"
              :show-icons="showIcons"
              :is-super-admin="isSuperAdmin"
              :is-admin="isAdmin"
              @settings="openSettings"
              @delete="handleDeleteClick"
              @users="handleUsersClick"
            />
          </div>
        </div>
      </div>
    </template>

    <ToolSettingsModal
      ref="settingsModal"
      :is-edit-mode="isEditMode"
      :tool-data="toolFormData"
      @save="saveSettings"
      @upload-icon="uploadIcon"
      @upload-image="uploadImage"
    />

    <ToolDeleteModal
      ref="deleteModal"
      :tool-name="selectedToolName"
      @confirm="deleteTool"
    />

    <ToolUsersModal
      ref="usersModal"
      :users="users"
      :assigned-users="selectedToolUsers"
      :claims="selectedToolClaims"
      :current-user-state="user.claims.state"
      @toggle-user="setAsUser"
      @show-claims="showClaimsModal"
    />

    <SetupClaims
      :selectedUser="selectedUser"
      :selectedTool="selectedTool"
      :claims="tools.find((el) => el.id === selectedTool)?.data.claims"
      @open-modal-users="$refs.usersModal.show()"
    />
  </div>
</template>

<script>
  import {mapGetters} from "vuex";
  import {httpsCallable} from "firebase/functions";
  import {
    collection,
    query,
    where,
    orderBy,
    getDocs,
    doc,
    updateDoc,
    setDoc,
    addDoc,
  } from "firebase/firestore";
  import {ref as storageRef, uploadBytesResumable} from "firebase/storage";
  import {firestore, functions, storage} from "../plugins/firebase";
  import Loading from "./LoadingComponent.vue";
  import isJSON from "validator/lib/isJSON";
  import SetupClaims from "./smallComponents/setupClaims.vue";
  import EmptyState from "./settings/EmptyState.vue";
  import ToolCard from "./settings/ToolCard.vue";
  import ToolListItem from "./settings/ToolListItem.vue";
  import ToolSearchBar from "./settings/ToolSearchBar.vue";
  import ToolToolbar from "./settings/ToolToolbar.vue";
  import ToolSettingsModal from "./settings/ToolSettingsModal.vue";
  import ToolDeleteModal from "./settings/ToolDeleteModal.vue";
  import ToolUsersModal from "./settings/ToolUsersModal.vue";
  import {
    ActivityType,
    logToolChange,
  } from "../services/activityLogger";
  import {setToolUserAccess} from "../services/toolAccessService";

  export default {
    name: "SettingsComponent",
    components: {
      Loading,
      SetupClaims,
      EmptyState,
      ToolCard,
      ToolListItem,
      ToolSearchBar,
      ToolToolbar,
      ToolSettingsModal,
      ToolDeleteModal,
      ToolUsersModal,
    },
    computed: {
      ...mapGetters({
        user: "user",
      }),
      filteredTools() {
        const term = (this.searchTerm || "").trim().toLowerCase();
        if (!term) {
          return this.tools;
        }
        return this.tools.filter((tool) => {
          const name = tool?.data?.name?.toLowerCase() || "";
          const description = tool?.data?.description?.toLowerCase() || "";
          return name.includes(term) || description.includes(term);
        });
      },
      hasNoTools() {
        return (
          this.hasLoadedOnce &&
          !this.loading &&
          !this.error &&
          this.tools.length === 0
        );
      },
      hasNoFilteredResults() {
        return (
          !this.loading &&
          !this.error &&
          this.tools.length > 0 &&
          this.filteredTools.length === 0
        );
      },
      hasErrorWithoutData() {
        return !!this.error && !this.loading && !this.hasLoadedOnce;
      },
      errorMessage() {
        if (!this.error) {
          return "";
        }
        return this.error.message || this.$t("settings.error_loading");
      },
      isSuperAdmin() {
        return this.user.claims.state === "superadmin";
      },
      isAdmin() {
        return this.user.claims.state === "admin";
      },
      selectedToolName() {
        return (
          this.tools.find((el) => el.id === this.selectedTool)?.data.name || ""
        );
      },
      selectedToolUsers() {
        return (
          this.tools.find((el) => el.id === this.selectedTool)?.data.users ||
          []
        );
      },
      selectedToolClaims() {
        return (
          this.tools.find((el) => el.id === this.selectedTool)?.data.claims ||
          []
        );
      },
      toolFormData() {
        return {
          name: this.name,
          description: this.description,
          url: this.url,
          iconName: this.iconName,
          iconPath: this.iconPath,
          imageName: this.imageName,
          imagePath: this.imagePath,
          jsonToken: this.jsonToken,
          claims: this.claims,
          maintenance: {
            enabled: this.maintenanceEnabled,
          },
        };
      },
    },
    data() {
      return {
        loading: true,
        tools: [],
        selectedTool: "",
        error: null,
        hasLoadedOnce: false,
        jsonToken: null,
        users: [],
        claims: [],
        timestamp: Date.parse(new Date()),
        name: "",
        description: "",
        url: "",
        iconName: "",
        iconPath: "",
        imageName: "",
        imagePath: "",
        maintenanceEnabled: false,
        selectedUser: {
          uid: null,
          displayName: null,
        },
        searchTerm: "",
        showIcons:
          localStorage.getItem("clgmbh-portal-app-item-icons") === "true" ||
          false,
        listOrCard:
          localStorage.getItem("clgmbh-portal-settings-display") || "card",
        isEditMode: false,
      };
    },
    watch: {
      listOrCard(val) {
        localStorage.setItem("clgmbh-portal-settings-display", val);
      },
      showIcons(val) {
        localStorage.setItem(
          "clgmbh-portal-app-item-icons",
          val ? "true" : "false"
        );
      },
    },
    methods: {
      normalizeClaim(claim) {
        return {
          key: claim?.key || "",
          value: claim?.value || "",
          required: !!claim?.required,
        };
      },
      buildClaimsMap(claims) {
        const map = new Map();
        (claims || []).forEach((claim) => {
          const normalized = this.normalizeClaim(claim);
          if (!normalized.key) {
            return;
          }

          map.set(normalized.key, normalized);
        });
        return map;
      },
      getClaimsDelta(beforeClaims = [], afterClaims = []) {
        const beforeMap = this.buildClaimsMap(beforeClaims);
        const afterMap = this.buildClaimsMap(afterClaims);

        const delta = {
          added: [],
          removed: [],
          changed: [],
        };

        afterMap.forEach((afterClaim, key) => {
          const beforeClaim = beforeMap.get(key);
          if (!beforeClaim) {
            delta.added.push(afterClaim);
            return;
          }

          if (
            beforeClaim.value !== afterClaim.value ||
            beforeClaim.required !== afterClaim.required
          ) {
            delta.changed.push({
              key,
              before: beforeClaim,
              after: afterClaim,
            });
          }
        });

        beforeMap.forEach((beforeClaim, key) => {
          if (!afterMap.has(key)) {
            delta.removed.push(beforeClaim);
          }
        });

        return delta;
      },
      hasClaimsDelta(claimsDelta) {
        return (
          (claimsDelta?.added?.length || 0) > 0 ||
          (claimsDelta?.removed?.length || 0) > 0 ||
          (claimsDelta?.changed?.length || 0) > 0
        );
      },
      handleDeleteClick(toolId) {
        this.selectedTool = toolId;
        this.$refs.deleteModal.show();
      },
      handleUsersClick(toolId) {
        this.selectedTool = toolId;
        this.$refs.usersModal.show();
      },
      showClaimsModal(toolUser) {
        this.selectedUser = {
          uid: toolUser.uid,
          name: toolUser.displayName,
        };
        this.$refs.usersModal.hide();
      },
      async loadData({showSpinner = true} = {}) {
        if (showSpinner) {
          this.loading = true;
        }
        this.error = null;

        try {
          const qOnlyMy = query(
            collection(firestore, "tools"),
            where("users", "array-contains", this.user.data.uid),
            orderBy("name")
          );
          const qAll = query(collection(firestore, "tools"), orderBy("name"));
          const q = this.user.claims.state === "superadmin" ? qAll : qOnlyMy;

          const querySnapshot = await getDocs(q);
          const tools = [];
          querySnapshot.forEach((docItem) => {
            tools.push({id: docItem.id, data: docItem.data()});
          });

          this.tools = tools;
          this.hasLoadedOnce = true;
        } catch (err) {
          console.error(err);
          this.error = err;
          this.$store.commit("addError", err);
        } finally {
          if (showSpinner || !this.hasLoadedOnce) {
            this.loading = false;
          }
        }
      },
      resetToolState() {
        this.name = "";
        this.url = "";
        this.claims = [];
        this.jsonToken = null;
        this.selectedTool = null;
        this.description = "";
        this.iconName = "";
        this.iconPath = "";
        this.imageName = "";
        this.imagePath = "";
        this.maintenanceEnabled = false;
      },
      reloadTools() {
        this.loadData();
      },
      openSettings(item) {
        this.resetToolState();
        this.timestamp = Date.now();
        this.selectedTool = item.id;
        this.isEditMode = true;
        this.name = item.data.name;
        this.description = item.data.description || "";
        this.url = item.data.url;
        this.claims = (item.data.claims || []).map((claim) => ({
          key: claim?.key || "",
          value: claim?.value || "",
          required: !!claim?.required,
        }));
        this.iconPath = item.data?.icon || "";
        this.imagePath = item.data.image || "";
        this.maintenanceEnabled = !!item.data?.maintenance?.enabled;
        this.$refs.settingsModal.show();
      },
      deleteThisClaim(index) {
        this.claims.splice(index, 1);
      },
      async saveSettings(formData) {
        if (formData.name === "" || formData.url === "") {
          this.$store.commit("addError", this.$t("settings.name_url_required"));
          return;
        }

        try {
          if (this.isEditMode) {
            const oldTool = this.tools.find((el) => el.id === this.selectedTool)?.data;
            const oldClaims = oldTool?.claims || [];
            const newClaims = formData.claims || [];
            const claimsDelta = this.getClaimsDelta(oldClaims, newClaims);
            const maintenanceBefore = {
              enabled: !!oldTool?.maintenance?.enabled,
            };
            const maintenanceAfter = {
              enabled: !!formData?.maintenance?.enabled,
            };
            const maintenanceChanged =
              maintenanceBefore.enabled !== maintenanceAfter.enabled;
            
            // Parallele Ausführung von Tool-Update und Token-Update (falls vorhanden)
            const updates = [
              updateDoc(doc(firestore, "tools", this.selectedTool), {
                name: formData.name,
                description: formData.description,
                url: formData.url,
                claims: formData.claims,
                image: formData.imagePath,
                icon: formData.iconPath,
                maintenance: {
                  enabled: !!formData?.maintenance?.enabled,
                },
              })
            ];

            // Nur Token aktualisieren, wenn es gefüllt und gültig ist
            if (formData.jsonToken && formData.jsonToken.trim() !== "" && isJSON(formData.jsonToken)) {
              updates.push(
                setDoc(
                  doc(firestore, "tools", this.selectedTool, "token", "data"),
                  JSON.parse(formData.jsonToken)
                )
              );
            }

            await Promise.all(updates);

            this.$store.commit(
              "addSuccess",
              this.$t("settings.success_update", {name: formData.name})
            );
            
            // Log activity
            await logToolChange(
              ActivityType.TOOL_UPDATED,
              this.selectedTool,
              formData.name,
              {
                name: oldTool?.name,
                description: oldTool?.description,
                url: oldTool?.url,
                image: oldTool?.image,
                icon: oldTool?.icon,
                claims: oldClaims,
                maintenance: maintenanceBefore,
              },
              {
                name: formData.name,
                description: formData.description,
                url: formData.url,
                image: formData.imagePath,
                icon: formData.iconPath,
                claims: newClaims,
                maintenance: maintenanceAfter,
              }
            );

            if (this.hasClaimsDelta(claimsDelta)) {
              await logToolChange(
                ActivityType.TOOL_CLAIMS_CHANGED,
                this.selectedTool,
                formData.name,
                {claims: oldClaims},
                {claims: newClaims},
                {claimsDelta}
              );
            }

            if (maintenanceChanged) {
              await logToolChange(
                ActivityType.TOOL_MAINTENANCE_CHANGED,
                this.selectedTool,
                formData.name,
                {maintenance: maintenanceBefore},
                {maintenance: maintenanceAfter}
              );
            }
          } else {
            const docRef = await addDoc(collection(firestore, "tools"), {
              name: formData.name,
              description: formData.description,
              url: formData.url,
              claims: formData.claims,
              users: [],
              image: formData.imagePath,
              icon: formData.iconPath,
              maintenance: {
                enabled: !!formData?.maintenance?.enabled,
              },
            });

            // Nur Token setzen, wenn es gefüllt und gültig ist
            if (formData.jsonToken && formData.jsonToken.trim() !== "" && isJSON(formData.jsonToken)) {
              await setDoc(
                doc(firestore, "tools", docRef.id, "token", "data"),
                JSON.parse(formData.jsonToken)
              );
            }

            this.$store.commit(
              "addSuccess",
              this.$t("settings.success_create", {name: formData.name})
            );
            
            // Log activity
            await logToolChange(
              ActivityType.TOOL_CREATED,
              docRef.id,
              formData.name,
              null,
              {
                name: formData.name,
                description: formData.description,
                url: formData.url,
                image: formData.imagePath,
                icon: formData.iconPath,
                claims: formData.claims,
                maintenance: {
                  enabled: !!formData?.maintenance?.enabled,
                },
              }
            );

            if ((formData.claims || []).length > 0) {
              await logToolChange(
                ActivityType.TOOL_CLAIMS_CHANGED,
                docRef.id,
                formData.name,
                {claims: []},
                {claims: formData.claims},
                {
                  claimsDelta: {
                    added: formData.claims,
                    removed: [],
                    changed: [],
                  },
                }
              );
            }

            if (formData?.maintenance?.enabled) {
              await logToolChange(
                ActivityType.TOOL_MAINTENANCE_CHANGED,
                docRef.id,
                formData.name,
                {
                  maintenance: {
                    enabled: false,
                  },
                },
                {
                  maintenance: {
                    enabled: !!formData?.maintenance?.enabled,
                  },
                }
              );
            }
          }

          this.$refs.settingsModal.hide();
          await this.loadData({showSpinner: false});
          this.resetToolState();
        } catch (err) {
          console.error(err);
          this.error = err;
          this.$store.commit("addError", err);
        }
      },
      async getUsers() {
        try {
          const getUsersFromAuth = httpsCallable(functions, "getUsers");
          const result = await getUsersFromAuth();
          const users = Array.isArray(result?.data?.users)
            ? result.data.users
            : [];

          this.users = users.sort((a, b) => {
            const nameA = (a?.displayName || "").toLowerCase();
            const nameB = (b?.displayName || "").toLowerCase();
            return nameA.localeCompare(nameB);
          });
        } catch (err) {
          console.error(err);
          this.$store.commit("addError", err);
        }
      },
      openModalCreate() {
        this.resetToolState();
        this.timestamp = Date.now();
        this.isEditMode = false;
        this.$refs.settingsModal.show();
      },
      uploadImage(data) {
        const image = data.target.files[0];
        const path = `preview/${this.timestamp}/${image.name}`;
        const useRef = storageRef(storage, path);
        const uploadTask = uploadBytesResumable(useRef, image);

        uploadTask.on(
          "state_changed",
          () => {
            this.imageName = this.$t("common.uploading");
          },
          (err) => {
            console.error(err);
            this.$store.commit("addError", err);
            this.imageName = this.$t("common.error");
          },
          () => {
            this.imageName = image.name;
            this.$store.commit(
              "addSuccess",
              this.$t("settings.success_upload", {name: this.imageName})
            );
            this.imagePath = path;
          }
        );
      },
      async deleteTool() {
        const recursiveDelete = httpsCallable(functions, "recursiveDelete");
        const toolName = this.selectedToolName;
        const toolData = this.tools.find((el) => el.id === this.selectedTool)?.data;
        
        try {
          await recursiveDelete({id: this.selectedTool});
          this.$store.commit(
            "addSuccess",
            this.$t("settings.success_delete", {name: toolName})
          );
          
          // Log activity
          await logToolChange(
            ActivityType.TOOL_DELETED,
            this.selectedTool,
            toolName,
            {
              name: toolData?.name,
              description: toolData?.description,
              url: toolData?.url,
              image: toolData?.image,
              icon: toolData?.icon
            },
            null
          );
        } catch (err) {
          console.error(err);
          this.$store.commit("addError", err);
        } finally {
          await this.loadData({showSpinner: false});
        }
      },
      async setAsUser(uid) {
        const tool = this.selectedTool;
        const state = !this.selectedToolUsers.includes(uid);
        const toolData = this.tools.find((el) => el.id === tool)?.data;
        const claimsRequired = (toolData?.claims || []).find((el) => el?.required);
        const targetUser = this.users.find((el) => el.uid === uid);
        const userName = targetUser?.displayName || uid;
        const userEmail = targetUser?.email || uid;

        if (!toolData) {
          return;
        }

        if (!Array.isArray(toolData.users)) {
          toolData.users = [];
        }

        try {
          if (state && !toolData.users.includes(uid)) {
            toolData.users.push(uid);
          }

          if (!state && toolData.users.includes(uid)) {
            toolData.users = toolData.users.filter((u) => u !== uid);
          }

          await setToolUserAccess({
            toolId: tool,
            toolName: toolData.name,
            uid,
            accessGranted: state,
            requiredClaim: claimsRequired,
            userEmail,
            userName,
          });
        } catch (err) {
          console.error(err);
          this.$store.commit("addError", err);
        }
      },
      uploadIcon(data) {
        const image = data.target.files[0];
        const path = `icon/${this.timestamp}/${image.name}`;
        const useRef = storageRef(storage, path);
        const uploadTask = uploadBytesResumable(useRef, image);

        uploadTask.on(
          "state_changed",
          () => {
            this.iconName = this.$t("common.uploading");
          },
          (err) => {
            console.error(err);
            this.$store.commit("addError", err);
            this.iconName = this.$t("common.error");
          },
          () => {
            this.iconName = image.name;
            this.$store.commit(
              "addSuccess",
              this.$t("settings.success_upload", {name: this.iconName})
            );
            this.iconPath = path;
          }
        );
      },
    },
    async mounted() {
      await Promise.allSettled([this.getUsers(), this.loadData()]);
    },
  };
</script>

<style>
  /* Styles removed - now in child components */
</style>
